import React, { useState } from "react"
import { Button, Form, FormGroup, Input, Label, Collapse } from "reactstrap"
import moment from "moment"
import { UpdateCPSRequest, UploadCPSAttachment } from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"

const CPSProduction = ({ entry, myUser, statusIndex, reload }) => {
  const setLoading = useLoading()
  const [isOpen, setIsOpen] = useState(false)
  const [productionReport, setProductionReport] = useState(null)
  const [trackingNumber, setTrackingNumber] = useState("")
  const [shippingService, setShippingService] = useState("")
  const [isUploading, setIsUploading] = useState(false)

  // Determine if this step is active based on entry statusIndex
  // For requests without samples, production starts at statusIndex 6
  // For requests with samples, production starts at statusIndex 8
  const isActive = entry?.statusIndex === (entry?.sampleRequired ? 8 : 6)
  const isCompleted = entry?.statusIndex > (entry?.sampleRequired ? 8 : 6)
  const isCPSLab = myUser?.company?.id === entry?.cpsLabID
  const isWarehouseUser = myUser?.cpsRole === "Warehouse"
  const canUpload = isActive && !isCompleted && isCPSLab && !isWarehouseUser

  // Only show "Complete & Ship" for one-time needs
  const showCompleteShip = entry?.needType === "one-time"

  const handleUploadReport = async e => {
    e.preventDefault()
    if (!productionReport || !trackingNumber || !shippingService) return

    setIsUploading(true)
    setLoading(true)

    try {
      // Upload the production report file
      const formData = new FormData()
      formData.append("file", productionReport)
      formData.append("id", entry?.id)
      const uploadResult = await UploadCPSAttachment(formData)

      // Update the request with production report and move to next status
      const updatedEntry = {
        ...entry,
        reportURL: uploadResult?.url,
        trackingNumber: trackingNumber,
        shippingService: shippingService,
        productionCompletedDate: new Date().toISOString(),
        statusIndex: entry?.needType === "one-time" ? 10 : 9, // Completed for one-time, Stock Management for annual
      }

      await UpdateCPSRequest(updatedEntry)

      setIsUploading(false)
      setProductionReport(null)
      setTrackingNumber("")
      setShippingService("")
      setIsOpen(false)
      setLoading(false)

      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        reload()
      }, 500)
    } catch (error) {
      console.error("Error uploading production report:", error)
      setIsUploading(false)
      setLoading(false)
      alert("Error uploading production report. Please try again.")
    }
  }

  return (
    <li className={"event-list " + (isActive ? "active" : "")}>
      <div
        className={"event-timeline-dot " + (isActive ? "bx-fade-right" : "")}
      >
        <i className="bx bx-right-arrow-circle font-size-18"></i>
      </div>
      <div className="d-flex">
        <div className="flex-shrink-0 me-3">
          <h5 className="font-size-14">
            {isCompleted ? moment(new Date()).format("DD.MM.YYYY") : ""}{" "}
            <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
          </h5>
        </div>
        <div className="flex-grow-1">
          <div>
            Production & Shipping{" "}
            {isActive
              ? ` - production of ${
                  entry?.needType === "one-time" ? "full quantity" : "batch"
                } in progress`
              : isCompleted
              ? ` - ${
                  entry?.needType === "one-time"
                    ? "full batch"
                    : "production batch"
                } completed and shipped`
              : ""}
          </div>
        </div>
      </div>

      {isCompleted && entry.reportURL && (
        <div className="mt-3">
          <a
            href={entry.reportURL}
            target="_blank"
            rel="noopener noreferrer"
            className="d-flex align-items-center text-decoration-none"
            style={{ color: "inherit" }}
          >
            <div style={{ width: "45px" }}>
              <div className="avatar-sm">
                <span className="avatar-title rounded-circle bg-success bg-soft text-success font-size-24">
                  <i className="bx bxs-check-circle" />
                </span>
              </div>
            </div>
            <div className="ms-4">
              <h5 className="font-size-14 mb-1">Production Certificate</h5>
              <small>Click To Download</small>
            </div>
            <div className="text-center ms-4">
              <i className="bx bx-download h3 m-0" />
            </div>
          </a>

          {(entry.trackingNumber || entry.shippingService) && (
            <div className="d-flex align-items-center mt-3">
              <div style={{ width: "45px" }}>
                <div className="avatar-sm">
                  <span className="avatar-title rounded-circle bg-info bg-soft text-info font-size-24">
                    <i className="bx bx-package" />
                  </span>
                </div>
              </div>
              <div className="ms-4">
                <h5 className="font-size-14 mb-1">Production Shipped</h5>
                {entry.trackingNumber && (
                  <div className="mt-1">
                    <small className="text-muted">
                      <i className="bx bx-package me-1"></i>
                      Tracking: {entry.trackingNumber}
                    </small>
                  </div>
                )}
                {entry.shippingService && (
                  <div>
                    <small className="text-muted">
                      <i className="bx bx-truck me-1"></i>
                      Service: {entry.shippingService}
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {canUpload && showCompleteShip && !isCompleted && (
        <div className="mt-3">
          <Button color="success" size="sm" onClick={() => setIsOpen(!isOpen)}>
            <i className="bx bx-package font-size-12 me-1"></i>
            Complete & Ship
          </Button>

          <Collapse isOpen={isOpen}>
            <div className="mt-3 p-3 border rounded">
              <Form onSubmit={handleUploadReport}>
                <FormGroup className="mb-2">
                  <Label>Production Certificate</Label>
                  <Input
                    type="file"
                    onChange={e => setProductionReport(e.target.files[0])}
                    accept=".pdf,.doc,.docx"
                    disabled={isUploading}
                  />
                </FormGroup>
                <FormGroup className="mb-2">
                  <Label>Shipping Tracking Number</Label>
                  <Input
                    type="text"
                    value={trackingNumber}
                    onChange={e => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number"
                    disabled={isUploading}
                  />
                </FormGroup>
                <FormGroup className="mb-2">
                  <Label>Shipping Service</Label>
                  <Input
                    type="text"
                    value={shippingService}
                    onChange={e => setShippingService(e.target.value)}
                    placeholder="Enter shipping service (e.g., DHL, UPS, FedEx)"
                    disabled={isUploading}
                  />
                </FormGroup>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    color="success"
                    size="sm"
                    disabled={
                      !productionReport ||
                      !trackingNumber ||
                      !shippingService ||
                      isUploading
                    }
                  >
                    {isUploading ? "Processing..." : "Complete Production"}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </Collapse>
        </div>
      )}

      {isActive && isCPSLab && isWarehouseUser && (
        <div className="mt-3">
          <div className="alert alert-warning mb-0">
            <i className="bx bx-info-circle me-2"></i>
            Only CPS Admin users can complete production and upload certificates.
            This step is read-only for warehouse users.
          </div>
        </div>
      )}
    </li>
  )
}

export default CPSProduction
